import { onMounted, onBeforeUnmount } from "vue";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";

export type HistoryAction = "undo" | "redo";

/** Ignore shortcuts while the user is typing into a field. */
const isEditingText = (target: EventTarget | null): boolean => {
	const element = target as HTMLElement | null;
	if (!element) return false;

	return (
		["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName) ||
		element.isContentEditable
	);
};

/**
 * The history action a keystroke asks for, or `null` when it asks for none.
 *
 * Exported separately so the mapping can be tested without mounting anything.
 */
export const historyActionForEvent = (
	event: KeyboardEvent
): HistoryAction | null => {
	if (!(event.metaKey || event.ctrlKey)) return null;

	const key = event.key.toLowerCase();

	if (key !== "z" && key !== "y") return null;
	if (isEditingText(event.target)) return null;

	return key === "y" || event.shiftKey ? "redo" : "undo";
};

/**
 * Binds Ctrl/Cmd+Z and Ctrl+Shift+Z / Ctrl+Y to the store's history for as long
 * as the calling component is mounted.
 */
export const useRuleHistoryShortcuts = () => {
	const store = useRuleEnginedStore();

	const onKeydown = (event: KeyboardEvent) => {
		const action = historyActionForEvent(event);

		// Only claim the keystroke once it maps to an action, so anything else
		// keeps its native behaviour.
		if (!action) return;

		event.preventDefault();

		if (action === "redo") {
			store.redo();
		} else {
			store.undo();
		}
	};

	onMounted(() => window.addEventListener("keydown", onKeydown));
	onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
};
